/* Per-size back-in-stock capture.
 *
 * WHY THE CLICK LISTENER IS ON .variant-input AND NOT ON THE RADIO
 * ----------------------------------------------------------------
 * Three things fight over the sold-out radios on this template:
 *   1. snippets/variant-button.liquid    adds a .disabled CLASS (server-side)
 *   2. assets/products-anywhere.js:19-24  sets input.disabled = true
 *   3. assets/theme.js:5457-5468         sets input.disabled = false again on
 *      every variant change, keeping only the class ("remains selectable")
 * So a sold-out radio is disabled on first paint and NOT disabled after the
 * shopper changes size once. Anything keyed on input.disabled is unreliable.
 * A <label> whose control is disabled still dispatches a bubbling click, so we
 * bind to the wrapping .variant-input div in the CAPTURE phase and touch
 * neither of those two files.
 *
 * WHY HIDING USES A CLASS AND NOT THE `hidden` ATTRIBUTE
 * -----------------------------------------------------
 * `hidden` relies on the UA rule [hidden]{display:none}, which any author
 * rule of equal specificity beats. The theme's own button and card styles do
 * exactly that, so `el.hidden = true` silently does nothing on them. The
 * .bis-active class in the section CSS carries !important instead.
 */
(function () {
  'use strict';

  /* Both the ultimate-products-2 and products-anywhere sections load this,
   * and a page can carry both. The listeners are on document, so running
   * twice would double-handle every click. */
  if (window.__siabBisInit) return;
  window.__siabBisInit = true;

  var REVISION = '2026-07-15';
  var LSKEY = 'siab_bis_v1';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function seen() {
    try { return JSON.parse(localStorage.getItem(LSKEY)) || {}; }
    catch (e) { return {}; }
  }

  function remember(variantId) {
    try {
      var s = seen();
      s[variantId] = Date.now();
      localStorage.setItem(LSKEY, JSON.stringify(s));
    } catch (e) { /* private mode: not worth failing over */ }
  }

  /* Scope every lookup to the owning form. This template renders MORE THAN ONE
   * product form per page (mens + kids), so document-level id lookups would hit
   * the wrong product. */
  function formOf(el) { return el.closest('form') || document; }
  function bisOf(el) { return formOf(el).querySelector('[data-bis]'); }

  /* The delivery estimate ("Get it between: ...") is rendered OUTSIDE the
   * product form, so the .bis-active class on the form cannot reach it with a
   * descendant rule. Match it by product scope instead and hide it while a
   * sold-out size is selected - promising a delivery window on a size we
   * cannot ship reads as a promise we cannot keep, directly under a CTA whose
   * whole job is to be honest about stock.
   *
   * Class, never the `hidden` attribute, for the reason described at the top
   * of this file. */
  function toggleDelivery(bis, hide) {
    var scope = bis.dataset.bisScope;
    if (!scope) return;
    var els = document.querySelectorAll('[data-bis-delivery="' + scope + '"]');
    for (var i = 0; i < els.length; i++) {
      els[i].classList.toggle('is-bis-hidden', !!hide);
    }
  }

  function msg(bis, text, ok) {
    var m = bis.querySelector('[data-bis-msg]');
    m.textContent = text || '';
    m.className = 'bis__msg' + (text ? (ok ? ' bis__msg--ok' : ' bis__msg--err') : '');
  }

  function closePanel(bis) {
    bis.querySelector('[data-bis-panel]').hidden = true;
    bis.querySelector('[data-bis-cta]').hidden = false;
    msg(bis, '', true);
  }

  /* Make the sold-out size look selected. The radio is disabled so a click
   * cannot check it, and we preventDefault anyway, so do it by hand. */
  function markSelected(wrap) {
    var fieldset = wrap.closest('.variant-input-wrap') || formOf(wrap);
    var inputs = fieldset.querySelectorAll('.variant-input input[type="radio"]');
    for (var i = 0; i < inputs.length; i++) inputs[i].checked = false;
    var mine = wrap.querySelector('input[type="radio"]');
    if (mine) mine.checked = true;
    var wraps = fieldset.querySelectorAll('.variant-input');
    for (var j = 0; j < wraps.length; j++) wraps[j].classList.remove('is-bis-selected');
    wrap.classList.add('is-bis-selected');
  }

  function showFor(bis, variantId, sizeLabel, wrap) {
    bis.dataset.bisVariant = variantId;
    /* the block itself ships hidden; .bis[hidden] is declared explicitly in the
     * section CSS so toggling the attribute genuinely works on this element */
    bis.hidden = false;
    /* .bis-active on the form hides the plan picker + add-to-cart via CSS */
    formOf(bis).classList.add('bis-active');
    toggleDelivery(bis, true);
    if (wrap) markSelected(wrap);

    var lede = bis.querySelector('[data-bis-lede]');
    lede.textContent = (bis.dataset.bisLedeTpl || '').replace('%SIZE%', sizeLabel || '');

    if (seen()[variantId]) {
      bis.querySelector('[data-bis-cta]').hidden = true;
      bis.querySelector('[data-bis-panel]').hidden = false;
      bis.querySelector('.bis__row').hidden = true;
      msg(bis, bis.dataset.bisSuccess || '', true);
    } else {
      bis.querySelector('.bis__row').hidden = false;
      closePanel(bis);
    }
  }

  function restore(bis) {
    bis.hidden = true;
    formOf(bis).classList.remove('bis-active');
    toggleDelivery(bis, false);
    var wraps = formOf(bis).querySelectorAll('.variant-input.is-bis-selected');
    for (var i = 0; i < wraps.length; i++) wraps[i].classList.remove('is-bis-selected');
    closePanel(bis);
  }

  function subscribe(bis, email, variantId) {
    var url = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' +
      encodeURIComponent(bis.dataset.bisCompany);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', 'revision': REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email: email } } }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                /* Shopify variants address as integration:::catalog:::external_id */
                id: '$shopify:::$default:::' + variantId
              }
            }
          }
        }
      })
    });
  }

  function submit(bis) {
    if (bis.querySelector('[data-bis-hp]').value) return;   /* bot */

    var input = bis.querySelector('[data-bis-email]');
    var email = (input.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      msg(bis, bis.dataset.bisInvalid || '', false);
      input.focus();
      return;
    }

    var variantId = bis.dataset.bisVariant;
    if (!variantId) return;

    var btn = bis.querySelector('[data-bis-submit]');
    btn.disabled = true;
    bis.setAttribute('aria-busy', 'true');
    msg(bis, '', true);

    subscribe(bis, email, variantId).then(function (res) {
      /* 202 = accepted. Klaviyo dedupes server-side, so a repeat submit for the
       * same profile+variant also returns 202. Treat it as success. */
      if (res.status === 202 || res.ok) {
        remember(variantId);
        bis.querySelector('.bis__row').hidden = true;
        msg(bis, bis.dataset.bisSuccess || '', true);
      } else {
        res.text().then(function (t) {
          console.error('[bis] Klaviyo ' + res.status + ' ' + t.slice(0, 300));
        });
        msg(bis, bis.dataset.bisError || '', false);
      }
    }).catch(function (err) {
      console.error('[bis] network', err);
      msg(bis, bis.dataset.bisError || '', false);
    }).then(function () {
      btn.disabled = false;
      bis.removeAttribute('aria-busy');
    });
  }

  /* ---- wiring ---------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var wrap = e.target.closest('.variant-input');
    if (wrap) {
      var bis = bisOf(wrap);
      if (!bis) return;
      /* [data-bis-variant] also excludes the language switcher, which reuses
       * the .variant-input class with data-value="en"/"es". */
      if (wrap.hasAttribute('data-bis-variant')) {
        e.preventDefault();
        e.stopPropagation();
        showFor(bis, wrap.getAttribute('data-bis-variant'),
          wrap.getAttribute('data-bis-size'), wrap);
      } else {
        restore(bis);
      }
      return;
    }

    var cta = e.target.closest('[data-bis-cta]');
    if (cta) {
      var b = cta.closest('[data-bis]');
      cta.hidden = true;
      b.querySelector('[data-bis-panel]').hidden = false;
      b.querySelector('[data-bis-email]').focus();
      return;
    }

    var sub = e.target.closest('[data-bis-submit]');
    if (sub) submit(sub.closest('[data-bis]'));
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || !e.target.closest) return;
    var input = e.target.closest('[data-bis-email]');
    if (!input) return;
    e.preventDefault();
    submit(input.closest('[data-bis]'));
  });

  /* Deep link from the restock email: ?bis_variant=<id> preselects the size. */
  function deepLink() {
    var want = new URLSearchParams(location.search).get('bis_variant');
    if (!want) return;
    var wrap = document.querySelector('.variant-input[data-bis-variant="' + want + '"]');
    if (!wrap) return;
    var bis = bisOf(wrap);
    if (!bis) return;
    showFor(bis, want, wrap.getAttribute('data-bis-size'), wrap);
    bis.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', deepLink);
  } else {
    deepLink();
  }
})();
